import { inject, Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Observable, share, switchMap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngxs/store';
import { IssuerSelectors } from '../pages/issuer/issuer-store/issuer-selectors';
import { SessionTimeoutService } from 'fi-ui-common';

@Injectable({
  providedIn: 'root'
})
export class RealtimeClient {
  private hubConnection: signalR.HubConnection;
  private store = inject(Store);
  private http = inject(HttpClient);
  private timeoutService = inject(SessionTimeoutService);
  private subscriptions = {};

  start() {
    return this.http.post<{url: string, accessToken: string}>('signalr/negotiate', {}).pipe(
      switchMap(({url, accessToken}) => {
        const options = {
          withCredentials: true,
          accessTokenFactory: () => accessToken,
          skipNegotiation: false,
          transport: signalR.HttpTransportType.WebSockets,
        }
        this.hubConnection = new signalR.HubConnectionBuilder().withUrl(url, options).withStatefulReconnect().build();

        this.hubConnection.onclose(() => this.timeoutService.changeStatus(true));

        return this.connect();
      })
    )
  }

  connect(): Observable<any> {
    return new Observable<void>((observer) => {
      this.hubConnection
      .start()
      .then(() => {
        observer.next();
        observer.complete();
      })
      .catch((error) => {
        observer.error(error);
      });
    });
  }

  listen<T>(url: string, upd = false): Observable<T> {
    const topic = upd ? this.getTopic(url) : url;
    if (this.subscriptions[topic]) {
      return this.subscriptions[topic];
    }

    this.addToGroup(topic);
    this.subscriptions[topic] = new Observable<T>((observer) => {
      this.hubConnection.on(topic, (message: T) => {
        observer.next(JSON.parse(message as any));
      });
      observer.add(() => this.removeFromGroup(topic))
    }).pipe(
      share()
    );

    return this.subscriptions[topic];
  }

  addToGroup(group: string): void {
    this.http.post('signalr/addToGroup', { connectionId: this.hubConnection.connectionId, group }).subscribe();
  }

  removeFromGroup(group: string): void {
    this.hubConnection.off(group);
    this.subscriptions[group] = null;
    this.http.post('signalr/removeFromGroup', { connectionId: this.hubConnection.connectionId, group }).subscribe();
  }

  private getTopic(url: string): string {
    const id = this.store.selectSnapshot(IssuerSelectors.getSelectedEntityId)(true);
    if (this.store.selectSnapshot(IssuerSelectors.isBulkPricingMode)) {
      return `${url}/bulk/${id.bulkPricingId}`;
    } else {
      return `${url}/potentialIssuer/${id.issuerId}_${id.currencyId}`;
    }
  }
}
